import React, { useEffect, useState, useCallback } from 'react';
import Layout from 'src/layout';
import Container from '@/components/ui/container';
import TableSkeleton from '@/components/ui/tableSkeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import axiosInstance from '@/lib/axiosInstance';
import { toast } from 'react-toastify';
import {
    RiBankCardLine,
    RiCheckLine,
    RiCloseLine,
    RiRefreshLine,
    RiFilterLine,
    RiInformationLine,
    RiShieldCheckLine,
    RiErrorWarningLine
} from 'react-icons/ri';

const ListBankDetails = () => {
    const [bankList, setBankList] = useState([]);
    const [loadingAPI, setLoadingAPI] = useState(true);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, pending, active, rejected
    const [rejectionReason, setRejectionReason] = useState('');
    const [selectedBankDetailID, setSelectedBankDetailID] = useState(null);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [processingBankID, setProcessingBankID] = useState(null);

    const fetchBankDetails = useCallback(async () => {
        try {
            setLoadingAPI(true);
            setError('');
            const { data } = await axiosInstance.get('/admin/bank-details');
            if (data.success) {
                let filteredData = data.data || [];
                if (filterStatus !== 'all') {
                    filteredData = filteredData.filter(bank => bank.status === filterStatus);
                }
                setBankList(filteredData);
            } else {
                setError('Failed to fetch bank details');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch bank details');
        } finally {
            setLoadingAPI(false);
        }
    }, [filterStatus]);

    useEffect(() => {
        fetchBankDetails();
    }, [fetchBankDetails]);

    const handleApprove = async (bankDetailID) => {
        if (processingBankID) return;
        try {
            setProcessingBankID(bankDetailID);
            const { data } = await axiosInstance.put(`/admin/bank-details/${bankDetailID}/approve`);
            if (data.success) {
                toast.success('Bank details approved successfully');
                fetchBankDetails();
            } else {
                toast.error(data.message || 'Failed to approve bank details');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to approve bank details');
        } finally {
            setProcessingBankID(null);
        }
    };

    const handleReject = async () => {
        if (!selectedBankDetailID || processingBankID) return;
        try {
            setProcessingBankID(selectedBankDetailID);
            const { data } = await axiosInstance.put(`/admin/bank-details/${selectedBankDetailID}/reject`, {
                rejectionReason
            });
            if (data.success) {
                toast.success('Bank details rejected');
                setShowRejectionModal(false);
                setRejectionReason('');
                setSelectedBankDetailID(null);
                fetchBankDetails();
            } else {
                toast.error(data.message || 'Failed to reject bank details');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to reject bank details');
        } finally {
            setProcessingBankID(null);
        }
    };

    const handleOpenRejectionModal = (bankDetailID) => {
        setSelectedBankDetailID(bankDetailID);
        setShowRejectionModal(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'rejected': return 'bg-rose-50 text-rose-700 border-rose-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    return (
        <Layout active={'admin-bank-details'} title={'Bank Account Approvals'}>
            <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">

                {/* 🚀 HEADER BANNER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <RiBankCardLine className="text-indigo-600" /> Bank Account Approvals
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Verify, review, and approve payout bank account credentials submitted by vendor partners.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={fetchBankDetails}
                            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition"
                            title="Refresh Bank Accounts"
                        >
                            <RiRefreshLine className={loadingAPI ? 'animate-spin text-indigo-600' : ''} />
                        </button>
                    </div>
                </div>

                {/* 🔍 FILTER STATUS TABS */}
                <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                        {[
                            { key: 'all', label: 'All Accounts' },
                            { key: 'pending', label: 'Pending Approval' },
                            { key: 'active', label: 'Active / Approved' },
                            { key: 'rejected', label: 'Rejected' }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setFilterStatus(tab.key)}
                                className={`px-4 py-2 text-xs font-bold rounded-xl transition-all border ${
                                    filterStatus === tab.key
                                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="text-xs font-semibold text-gray-500">
                        Total Accounts Listed: <span className="font-bold text-gray-900">{bankList.length}</span>
                    </div>
                </div>

                {error && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl text-xs font-semibold">
                        {error}
                    </div>
                )}

                {/* 📋 TABLE CONTAINER */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {loadingAPI ? (
                        <TableSkeleton rows={5} />
                    ) : bankList.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <div className="w-16 h-16 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto mb-3">
                                <RiBankCardLine className="text-3xl" />
                            </div>
                            <h3 className="text-base font-bold text-gray-900">No Bank Accounts Found</h3>
                            <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                                No bank account submissions match your selected filter criteria.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                                        <th className="py-3.5 px-4">ID</th>
                                        <th className="py-3.5 px-4">Brand Partner</th>
                                        <th className="py-3.5 px-4">Account Holder</th>
                                        <th className="py-3.5 px-4">Account Number</th>
                                        <th className="py-3.5 px-4">IFSC Code</th>
                                        <th className="py-3.5 px-4">Bank Name & Branch</th>
                                        <th className="py-3.5 px-4 text-center">Status</th>
                                        <th className="py-3.5 px-4 text-center">Submitted Date</th>
                                        <th className="py-3.5 px-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-xs font-medium text-gray-700">
                                    {bankList.map((bank) => (
                                        <tr key={bank.bankDetailID} className="hover:bg-gray-50/80 transition">
                                            {/* ID */}
                                            <td className="py-3.5 px-4 font-mono text-[11px] text-gray-400">
                                                #{bank.bankDetailID}
                                            </td>

                                            {/* Brand */}
                                            <td className="py-3.5 px-4 font-bold text-gray-900">
                                                <div>{bank.brandName || 'Inhouse'}</div>
                                                <div className="text-[10px] text-gray-400 font-medium">@{bank.username}</div>
                                            </td>

                                            {/* Account Holder */}
                                            <td className="py-3.5 px-4 font-bold text-indigo-900">
                                                {bank.accountHolderName}
                                            </td>

                                            {/* Account Number */}
                                            <td className="py-3.5 px-4 font-mono font-bold text-gray-900">
                                                {bank.accountNumber}
                                            </td>

                                            {/* IFSC */}
                                            <td className="py-3.5 px-4 font-mono text-xs text-gray-600 uppercase">
                                                {bank.ifscCode}
                                            </td>

                                            {/* Bank Name */}
                                            <td className="py-3.5 px-4">
                                                <div className="font-bold text-gray-900">{bank.bankName}</div>
                                                {bank.branchName && (
                                                    <div className="text-[10px] text-gray-400">{bank.branchName}</div>
                                                )}
                                            </td>

                                            {/* Status */}
                                            <td className="py-3.5 px-4 text-center">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider ${getStatusColor(bank.status)}`}>
                                                    {bank.status === 'active' && <RiShieldCheckLine />}
                                                    {bank.status === 'pending' && <RiErrorWarningLine />}
                                                    {bank.status}
                                                </span>
                                            </td>

                                            {/* Date */}
                                            <td className="py-3.5 px-4 text-center text-gray-500">
                                                {formatDate(bank.createdAt)}
                                            </td>

                                            {/* Actions */}
                                            <td className="py-3.5 px-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    {bank.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleApprove(bank.bankDetailID)}
                                                                disabled={processingBankID !== null}
                                                                className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1 shadow-sm transition disabled:opacity-50"
                                                                title="Approve Bank Account"
                                                            >
                                                                <RiCheckLine /> Approve
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenRejectionModal(bank.bankDetailID)}
                                                                disabled={processingBankID !== null}
                                                                className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs flex items-center gap-1 shadow-sm transition disabled:opacity-50"
                                                                title="Reject Bank Account"
                                                            >
                                                                <RiCloseLine /> Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {bank.rejectionReason && (
                                                        <button
                                                            onClick={() => toast.info(`Rejection Reason: ${bank.rejectionReason}`)}
                                                            className="p-1.5 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition"
                                                            title="View Rejection Reason"
                                                        >
                                                            <RiInformationLine className="text-base" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* REJECTION REASON MODAL */}
            {showRejectionModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-5 border border-gray-100 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-gray-900">Reject Bank Details</h3>
                        <p className="text-xs text-gray-500">
                            Please provide a reason for rejecting these vendor bank account details.
                        </p>

                        <textarea
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                            rows={3}
                            placeholder="Enter rejection reason..."
                        />

                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                onClick={() => {
                                    setShowRejectionModal(false);
                                    setRejectionReason('');
                                    setSelectedBankDetailID(null);
                                }}
                                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={!rejectionReason.trim() || processingBankID !== null}
                                className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl disabled:opacity-50 shadow-sm"
                            >
                                {processingBankID ? 'Rejecting...' : 'Confirm Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default ListBankDetails;
